/**
 * DEV PLACEHOLDER TEXTURES — generated procedurally at runtime (Graphics → generateTexture).
 * They exist only so the UI is complete before the ui-vfx art builder ships final art. Every key is
 * stable: when a real texture with the same key is loaded by the manifest first, generation is
 * skipped, so swapping is a pure asset drop-in (keep the nine-slice insets in theme.ts `Slices`).
 */
import Phaser from 'phaser';
import { Palette } from './theme';

export const DEV_UI_TEXTURES = [
  'ui-scroll-panel',   // 48x48 nine-slice, parchment + lacquer border (insets 16)
  'ui-wave-strip',     // 16x6 repeating southern wave ornament (TileSprite along panel edges)
  'ui-paper-panel',    // 48x48 nine-slice letter paper (insets 12)
  'ui-lacquer-bar',    // 24x24 nine-slice lacquer button/selection bar (insets 6)
  'ui-indigo-bar',     // 24x24 nine-slice dim indigo bar (insets 6)
  'ui-portrait-frame', // 104x104 lacquered frame with transparent 96x96 window
  'ui-portrait-missing', // 96x96 silhouette placeholder when a portrait key is missing
  'ui-dpad',           // 120x120 virtual d-pad
  'ui-round-btn',      // 48x48 virtual button
  'ui-dot',            // 4x4 white pixel (particles, sliders)
  'ui-haze',           // 32x32 soft blob (title haze particles)
  'ui-rain',           // 2x10 pale streak (title drizzle particles)
  'ui-arrow-down',     // 12x8 lacquer continue indicator
  'ui-scroll-knob',    // 12x124 rolled scroll end (dialogue box left; flipX for the right)
] as const;
export type DevUiTexture = (typeof DEV_UI_TEXTURES)[number];

let logged = false;

/** Generates any missing dev textures. Idempotent and cheap. */
export function ensureDevUiTextures(scene: Phaser.Scene): void {
  const tex = scene.textures;
  const made: string[] = [];
  const gen = (key: DevUiTexture, w: number, h: number, draw: (g: Phaser.GameObjects.Graphics) => void) => {
    if (tex.exists(key)) return;
    const g = scene.make.graphics({ x: 0, y: 0 }, false);
    draw(g);
    g.generateTexture(key, w, h);
    g.destroy();
    made.push(key);
  };

  gen('ui-scroll-panel', 48, 48, (g) => {
    // Concept (style-anchors/dialogue-box-concept.png): parchment, lacquer red-brown border with a thin gold line.
    g.fillStyle(Palette.parchment, 1); g.fillRect(0, 0, 48, 48);
    g.fillStyle(Palette.parchmentDark, 0.3); g.fillRect(5, 5, 38, 38);
    g.fillStyle(Palette.parchment, 1); g.fillRect(7, 7, 34, 34);
    g.fillStyle(Palette.lacquerDark, 1); g.fillRect(0, 0, 48, 1); g.fillRect(0, 47, 48, 1); g.fillRect(0, 0, 1, 48); g.fillRect(47, 0, 1, 48);
    g.lineStyle(2, Palette.lacquer, 1); g.strokeRect(2, 2, 44, 44);
    g.lineStyle(1, Palette.sand, 1); g.strokeRect(4.5, 4.5, 39, 39);
    // small lacquer corner knots in the fixed corners
    g.fillStyle(Palette.lacquer, 1);
    for (const [x, y] of [[6, 6], [39, 6], [6, 39], [39, 39]] as const) { g.fillRect(x, y, 3, 3); g.fillStyle(Palette.sand, 1); g.fillRect(x + 1, y + 1, 1, 1); g.fillStyle(Palette.lacquer, 1); }
  });

  gen('ui-scroll-knob', 12, 124, (g) => {
    // Rolled scroll end: dark lacquer roll with teak caps and a sand highlight ridge.
    g.fillStyle(Palette.lacquerDark, 1); g.fillRect(2, 6, 8, 112);
    g.fillStyle(Palette.lacquer, 1); g.fillRect(3, 8, 6, 108);
    g.fillStyle(Palette.sand, 0.55); g.fillRect(4, 8, 1, 108);
    g.fillStyle(Palette.teak, 1); g.fillRect(0, 0, 12, 8); g.fillRect(0, 116, 12, 8);
    g.fillStyle(Palette.sand, 0.9); g.fillRect(2, 2, 8, 2); g.fillRect(2, 120, 8, 2);
    g.fillStyle(Palette.lacquerDark, 1); g.fillRect(0, 7, 12, 1); g.fillRect(0, 116, 12, 1);
  });

  gen('ui-wave-strip', 16, 6, (g) => {
    // Gold wave-and-knot ornament on a faint lacquer band.
    g.fillStyle(Palette.lacquer, 0.18); g.fillRect(0, 0, 16, 6);
    g.lineStyle(1, Palette.sand, 1);
    g.beginPath();
    g.moveTo(0, 4.5); g.lineTo(4, 1.5); g.lineTo(8, 4.5); g.lineTo(12, 1.5); g.lineTo(16, 4.5);
    g.strokePath();
    g.fillStyle(Palette.lacquer, 1); g.fillRect(3, 0, 2, 2); g.fillRect(11, 0, 2, 2);
    g.fillStyle(Palette.teak, 1); g.fillRect(7, 4, 2, 2); g.fillRect(15, 4, 1, 2);
  });

  gen('ui-paper-panel', 48, 48, (g) => {
    g.fillStyle(Palette.paperEdge, 1); g.fillRect(0, 0, 48, 48);
    g.fillStyle(Palette.paper, 1); g.fillRect(2, 2, 44, 44);
    g.fillStyle(Palette.parchmentDark, 0.25); g.fillRect(2, 44, 44, 2); g.fillRect(44, 2, 2, 44);
    // faint ruled hint in corners only (stays fixed)
    g.fillStyle(Palette.lacquer, 0.5); g.fillRect(4, 4, 3, 3); g.fillRect(41, 41, 3, 3);
  });

  gen('ui-lacquer-bar', 24, 24, (g) => {
    g.fillStyle(Palette.lacquerDark, 1); g.fillRect(0, 0, 24, 24);
    g.fillStyle(Palette.lacquer, 1); g.fillRect(1, 1, 22, 22);
    g.fillStyle(Palette.lacquerLight, 1); g.fillRect(2, 2, 20, 2);
    g.fillStyle(Palette.sand, 0.8); g.fillRect(2, 2, 2, 2); g.fillRect(20, 2, 2, 2);
  });

  gen('ui-indigo-bar', 24, 24, (g) => {
    g.fillStyle(Palette.night, 1); g.fillRect(0, 0, 24, 24);
    g.fillStyle(Palette.indigoDeep, 1); g.fillRect(1, 1, 22, 22);
    g.fillStyle(Palette.indigo, 1); g.fillRect(2, 2, 20, 2);
  });

  gen('ui-portrait-frame', 104, 104, (g) => {
    // Four bars so the 96x96 window stays transparent.
    g.fillStyle(Palette.lacquerDark, 1);
    g.fillRect(0, 0, 104, 4); g.fillRect(0, 100, 104, 4); g.fillRect(0, 0, 4, 104); g.fillRect(100, 0, 4, 104);
    g.fillStyle(Palette.lacquer, 1);
    g.fillRect(1, 1, 102, 2); g.fillRect(1, 101, 102, 2); g.fillRect(1, 1, 2, 102); g.fillRect(101, 1, 2, 102);
    g.fillStyle(Palette.sand, 0.9);
    for (const [x, y] of [[0, 0], [98, 0], [0, 98], [98, 98]] as const) g.fillRect(x + 1, y + 1, 4, 4);
  });

  gen('ui-portrait-missing', 96, 96, (g) => {
    g.fillStyle(Palette.parchmentDark, 1); g.fillRect(0, 0, 96, 96);
    g.fillStyle(Palette.inkSoft, 0.9);
    g.fillCircle(48, 36, 18);
    g.fillRoundedRect(20, 56, 56, 40, 12);
    g.lineStyle(1, Palette.lacquer, 0.9);
    for (let i = 4; i < 96; i += 8) { g.lineBetween(i, 1, i + 4, 1); g.lineBetween(i, 95, i + 4, 95); g.lineBetween(1, i, 1, i + 4); g.lineBetween(95, i, 95, i + 4); }
  });

  gen('ui-dpad', 120, 120, (g) => {
    g.fillStyle(Palette.night, 0.55);
    g.fillRoundedRect(40, 4, 40, 112, 8);
    g.fillRoundedRect(4, 40, 112, 40, 8);
    g.lineStyle(2, Palette.sand, 0.8);
    g.strokeRoundedRect(41, 5, 38, 110, 8);
    g.strokeRoundedRect(5, 41, 110, 38, 8);
    g.fillStyle(Palette.sand, 0.9);
    g.fillTriangle(60, 14, 50, 28, 70, 28);
    g.fillTriangle(60, 106, 50, 92, 70, 92);
    g.fillTriangle(14, 60, 28, 50, 28, 70);
    g.fillTriangle(106, 60, 92, 50, 92, 70);
    g.fillStyle(Palette.indigoDeep, 0.9); g.fillRect(52, 52, 16, 16);
  });

  gen('ui-round-btn', 48, 48, (g) => {
    g.fillStyle(Palette.night, 0.6); g.fillCircle(24, 24, 23);
    g.lineStyle(2, Palette.sand, 0.85); g.strokeCircle(24, 24, 22);
    g.fillStyle(Palette.indigoDeep, 0.9); g.fillCircle(24, 24, 18);
  });

  gen('ui-dot', 4, 4, (g) => { g.fillStyle(0xffffff, 1); g.fillRect(0, 0, 4, 4); });

  gen('ui-haze', 32, 32, (g) => {
    g.fillStyle(0xffffff, 0.08); g.fillCircle(16, 16, 16);
    g.fillStyle(0xffffff, 0.12); g.fillCircle(16, 16, 11);
    g.fillStyle(0xffffff, 0.16); g.fillCircle(16, 16, 6);
  });

  gen('ui-rain', 2, 10, (g) => { g.fillStyle(0xffffff, 1); g.fillRect(0, 0, 2, 10); g.fillStyle(0xffffff, 0.4); g.fillRect(0, 0, 2, 3); });

  gen('ui-arrow-down', 12, 8, (g) => { g.fillStyle(Palette.lacquer, 1); g.fillTriangle(0, 0, 12, 0, 6, 8); });

  if (made.length && !logged) {
    logged = true;
    console.info(`[ui] generated DEV placeholder textures (swap via manifest with the same keys): ${made.join(', ')}`);
  }
}
